import receitaFederalIconUrl from '@/assets/icons/pulse/receita-federal.svg';
import sanctionsIconUrl from '@/assets/icons/pulse/sanctions.svg';
import { CONCEPT_ICONS } from '@/constants/icons';
import type { EntityType } from '@/types/kyc';
import type { PulseCategoryCount } from '@/types/entityPulse';
import { getEntityTypeIcon } from '@/utils/kyc';

const humanizeSlug = (slug: string) => (
    slug
        .split('_')
        .filter(Boolean)
        .map((part) => (part.length <= 3 ? part.toUpperCase() : part[0]?.toUpperCase() + part.slice(1)))
        .join(' ')
);

/** A catalog/config slug as readable text (e.g. "pep_person" → "PEP Person"); empty for empty input. */
export const formatEntityPulseCatalogValue = (value: string | null | undefined) => {
    const trimmed = value?.trim();
    return trimmed ? humanizeSlug(trimmed) : '';
};

/** The event's catalog text, or its slug made readable when the catalog has no text for it. */
export const getEntityPulseEventLabel = (eventSlug: string, eventText?: string | null) => (
    eventText?.trim() || humanizeSlug(eventSlug)
);

// Keyed by the event catalog's event_category_slug.
const PULSE_EVENT_CATEGORY_ICONS: Record<string, string> = {
    pep: CONCEPT_ICONS.pep,
    lawsuits: CONCEPT_ICONS.lawsuits,
    ntl_sanctions: CONCEPT_ICONS.sanctionsNational,
    intl_sanctions: CONCEPT_ICONS.sanctionsInternational,
};

// Keyed by the monitoring config's category_slug (the category of a check). Custom SVG icons take
// precedence over the Material Symbols below (see PulseCategoryIcon).
const MONITORING_CATEGORY_CUSTOM_ICONS: Record<string, string> = {
    receita_federal: receitaFederalIconUrl,
    sanctions: sanctionsIconUrl,
};

const MONITORING_CATEGORY_ICONS: Record<string, string> = {
    pep: CONCEPT_ICONS.pep,
    legal: CONCEPT_ICONS.lawsuits,
    fiscal_tributario: CONCEPT_ICONS.fiscal,
    trabalhista: CONCEPT_ICONS.labor,
    midia_negativa: CONCEPT_ICONS.negativeMedia,
};

/** Custom SVG icon URL for a monitoring category, if it has one. */
export const getPulseMonitoringCategoryCustomIcon = (categorySlug: string | null | undefined): string | null => (
    MONITORING_CATEGORY_CUSTOM_ICONS[categorySlug ?? ''] ?? null
);

/** Material Symbol for a monitoring category (config category_slug) on Pulse screens. */
export const getPulseMonitoringCategoryIcon = (categorySlug: string | null | undefined): string => (
    MONITORING_CATEGORY_ICONS[categorySlug ?? ''] ?? 'monitoring'
);

/** Icon for a pulse event, by its catalog category (event_category_slug). */
export const getPulseEventCategoryIcon = (eventCategorySlug: string | null | undefined): string => (
    PULSE_EVENT_CATEGORY_ICONS[eventCategorySlug ?? ''] ?? 'notifications'
);

// pulse.policy_entities.exclude_reason → why the pipeline doesn't monitor an enrolled entity.
const EXCLUDE_REASON_LABELS: Record<string, string> = {
    not_valid: 'Documento inválido',
    not_exist: 'Não encontrada na Receita Federal',
    minor: 'Menor de idade',
};

export const getPulseExcludeReasonLabel = (excludeReason: string | null | undefined): string => (
    EXCLUDE_REASON_LABELS[excludeReason ?? ''] ?? (formatEntityPulseCatalogValue(excludeReason) || 'Sem motivo')
);

// A Pulse tax id type ('cnpj' / 'cpf', e.g. a policy's entity_tax_id_type) as the app's entity type.
export const pulseTaxIdTypeToEntityType = (taxIdType: string | null | undefined): EntityType | null => {
    if (taxIdType === 'cnpj') return 'company';
    if (taxIdType === 'cpf') return 'person';
    return null;
};

// Category count columns (Por entidade / Políticas tables): the table sorts a category column as
// "category:<slug>"; the API takes sort_field="category" + sort_category=<slug>.
const CATEGORY_SORT_PREFIX = 'category:';

export const toCategoryColumnField = (categorySlug: string) => `${CATEGORY_SORT_PREFIX}${categorySlug}`;

export const toTableSortField = (sortField: string | undefined, sortCategory: string | null) => (
    sortField === 'category' && sortCategory ? toCategoryColumnField(sortCategory) : sortField
);

export const fromTableSortField = (tableSortField: string): { sortField: string; sortCategory: string | null } => (
    tableSortField.startsWith(CATEGORY_SORT_PREFIX)
        ? { sortField: 'category', sortCategory: tableSortField.slice(CATEGORY_SORT_PREFIX.length) }
        : { sortField: tableSortField, sortCategory: null }
);

export const getCategoryCount = (counts: PulseCategoryCount[], categorySlug: string) => (
    counts.find((count) => count.category_slug === categorySlug)?.event_count ?? 0
);

/** A policy's display name; its short id when the pipeline has no name for it. */
export const getPulsePolicyLabel = (policy: { policy_id: string; policy_name: string | null }) => (
    policy.policy_name ?? `${policy.policy_id.slice(0, 8)}…`
);

/** The icon of the entity type a policy monitors (company / person); none for a mixed policy ('mix') or one
 * the pipeline hasn't typed yet (no entities). */
export const getPulsePolicyTypeIcon = (taxIdType: string | null | undefined): string | null => {
    const entityType = pulseTaxIdTypeToEntityType(taxIdType);
    return entityType ? getEntityTypeIcon(entityType) : null;
};
